import { View, Text } from 'tamagui'
import { QuestionCard } from './QuestionCard'
import { TrueFalseQuestion } from './TrueFalseQuestion'
import { IExamQuestion } from '@dtos/Activity'
import { useActivityStore } from '@stores/useActivityStore'
import { useCallback, useMemo } from 'react'
import { MultipleChoiceQuestion } from './MultipleChoiceQuestion'
import { AssociativeQuestion } from './AssociativeQuestion'

interface IDynamicQuestionProps {
  question: IExamQuestion
  questionIndex: number
}

function DynamicQuestion({ question, questionIndex }: IDynamicQuestionProps) {
  const { activity, isRetrying } = useActivityStore()

  const determineMode = useCallback((): 'edit' | 'readonly' | 'retry' => {
    // Se está em processo de retry, sempre retorna 'edit'
    if (isRetrying) return 'edit'

    const hasAnswer = !!activity?.examAnswer
    const canRetry =
      activity?.retryPermitted &&
      activity?.retryCount &&
      activity?.maxAttempts &&
      activity?.retryCount < activity?.maxAttempts

    if (hasAnswer && canRetry) return 'retry'

    if (!hasAnswer || canRetry) return 'edit'

    return 'readonly'
  }, [isRetrying, activity])

  const renderQuestionContent = useCallback(() => {
    const mode = determineMode()
    // Se está em retry, não passa previousAnswer para limpar completamente o form

    const previousAnswer = isRetrying
      ? undefined
      : activity?.examAnswer?.examQuestionAnswers?.find(
          (answer) => answer.examQuestionId === question.id,
        )

    switch (question.type) {
      case 'TrueOrFalseExamQuestion':
        return (
          <TrueFalseQuestion
            questionId={question.id}
            previousAnswer={
              previousAnswer
                ? {
                    selectedOption: previousAnswer.trueOrFalseOption || false,
                    correct: previousAnswer.correct,
                    score: previousAnswer.score,
                  }
                : undefined
            }
            mode={mode}
          />
        )

      case 'MultipleChoiceExamQuestion':
        return (
          <MultipleChoiceQuestion
            questionId={question.id}
            options={question.options || []}
            previousAnswer={
              previousAnswer
                ? {
                    selectedOption:
                      previousAnswer.multipleChoiceOption?.id || '',
                    correct:
                      previousAnswer.multipleChoiceOption?.correct || false,
                    score: previousAnswer.score,
                  }
                : undefined
            }
            mode={mode}
          />
        )

      case 'AssociativeExamQuestion':
        return (
          <AssociativeQuestion
            questionId={question.id}
            firstAssertions={question.firstAssertions || []}
            secondAssertions={question.secondAssertions || []}
            previousAnswer={
              previousAnswer
                ? {
                    associativeOptions: previousAnswer.associativeOptions || [],
                    correct: previousAnswer.correct,
                    score: previousAnswer.score,
                  }
                : undefined
            }
            mode={mode}
          />
        )

      default:
        return (
          <View p="$spacing-inset-xs">
            <Text color="$neutral-500">
              Tipo de questão não suportado: {question.type}
            </Text>
          </View>
        )
    }
  }, [determineMode, isRetrying, question, activity])

  return (
    <QuestionCard
      questionNumber={questionIndex + 1}
      title={question.title}
      value={question.point}
      statement={question.comment || ''}
    >
      {renderQuestionContent()}
    </QuestionCard>
  )
}

export function DynamicQuestions() {
  const { activity, isRetrying } = useActivityStore()

  const sortedQuestions = useMemo(() => {
    if (!activity?.examQuestions) return []
    return activity.examQuestions.sort((a, b) => a.order - b.order)
  }, [activity?.examQuestions])

  if (!sortedQuestions || sortedQuestions.length === 0) {
    return (
      <View p="$spacing-inset-lg">
        <Text textAlign="center" color="$neutral-500">
          Nenhuma questão encontrada para esta atividade.
        </Text>
      </View>
    )
  }

  return (
    <View gap="$spacing-inset-lg">
      {sortedQuestions.map((question, index) => (
        <DynamicQuestion
          key={`${question.id}-${isRetrying ? 'retry' : 'normal'}`}
          question={question}
          questionIndex={index}
        />
      ))}
    </View>
  )
}
